import { DateTime } from 'luxon';
import { WorkoutWithCycleAndSession } from './types';

/**
 * Determine the assigned date for a workout based on the workout's session and cycle sort orders
 * and the block's start date
 * @param match - the workout to get the assigned date for
 * @param numberOfDaysForCycle - the number of days in the cycle
 * @param firstMatchingSessionWithAssignedDate - (if exists) the first session from the first cycle
 */
export const getGeneratedAssignedDate = (
  blockStartDate: string,
  match: WorkoutWithCycleAndSession,
  numberOfDaysForCycle: number,
  firstSessionFromSameCycle: WorkoutWithCycleAndSession | undefined,
) => {
  const startDate = DateTime.fromISO(blockStartDate);
  if (!startDate.isValid) {
    throw new Error('Invalid start date');
  }

  const { cycle, session } = match;

  if (numberOfDaysForCycle === 7) {
    // If the cycle is exactly a week long
    // we can just use the start date and add the session and cycle sort orders
    return startDate
      .plus({ days: session.id - 1, weeks: cycle.id - 1 })
      .toISODate();
  }

  // otherwise determine the assigned date based off of the first session
  // ex: if session 1 is scheduled for a monday, then session 1 in the next cycle should also be a monday
  // and session 2 should be a tuesday
  if (firstSessionFromSameCycle && firstSessionFromSameCycle.assignedDate) {
    const delta =
      match.session.id < firstSessionFromSameCycle.session.id ? -1 : 1;
    // get either the previous or next week from the first matching session with an assigned date
    // depending on the delta of the session sort orders
    return DateTime.fromISO(firstSessionFromSameCycle.assignedDate)
      .plus({ days: delta * 7 * (cycle.id - 1) })
      .toISODate();
  }
  // otherwise fail to generate an assigned date
  return null;
};
